import React, { useEffect, useState } from 'react';
import { Form, Input, Select, InputNumber, Switch, Row, Col, Typography, message, Divider, Alert, Card } from 'antd';
import { masterDataService, Product, VendorProductLink } from '../services/masterDataService';
import ModernFormModal from './ModernFormModal';

const { TextArea } = Input;
const { Text } = Typography;

export interface QuickAddVendorProductModalProps {
  open: boolean;
  vendorId: number | undefined;
  vendorName?: string;
  onClose: () => void;
  /** Called after catalog line is created; use to refresh PO line picker and optionally pre-fill a row. */
  onCreated: (line: VendorProductLink) => void;
  /** Match PO create modal stacking when opened from Purchase Orders page. */
  getContainer?: () => HTMLElement;
  wrapClassName?: string;
}

/**
 * Vendor product form opened from the PO flow — same shell and layout patterns as Create Purchase Order (ModernFormModal + sections).
 */
const QuickAddVendorProductModal: React.FC<QuickAddVendorProductModalProps> = ({
  open,
  vendorId,
  vendorName,
  onClose,
  onCreated,
  getContainer,
  wrapClassName = 'modern-form-modal-wrap',
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      vendor_price: 0,
      lead_time_days: 7,
      minimum_order_quantity: 1,
      is_preferred: false,
      unit_of_measure: 'pcs',
    });
  }, [open, form]);

  useEffect(() => {
    if (!open || !vendorId) return;
    let cancelled = false;
    (async () => {
      try {
        const [all, linked] = await Promise.all([
          masterDataService.getProducts({ active_only: true, limit: 2000 }),
          masterDataService.getVendorProducts(vendorId),
        ]);
        if (cancelled) return;
        const taken = new Set(
          (linked || []).map((l) => l.product_id).filter((id): id is number => id != null),
        );
        setProducts((all || []).filter((p) => !taken.has(p.id)));
      } catch {
        setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, vendorId]);

  const handleFinish = async (v: Record<string, unknown>) => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const name = String(v.name ?? '').trim();
      const created = await masterDataService.addVendorProduct(vendorId, {
        name,
        vendor_product_code: (v.vendor_product_code as string)?.trim() || undefined,
        vendor_price: (v.vendor_price as number) ?? 0,
        lead_time_days: (v.lead_time_days as number) ?? 7,
        minimum_order_quantity: (v.minimum_order_quantity as number) ?? 1,
        is_preferred: (v.is_preferred as boolean) ?? false,
        product_id: (v.product_id as number) ?? undefined,
        description: (v.description as string)?.trim() || undefined,
        unit_of_measure: (v.unit_of_measure as string)?.trim() || 'pcs',
      });
      message.success('Vendor product saved — you can select it on the PO.');
      onCreated(created);
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string | unknown } } };
      const d = err?.response?.data?.detail;
      message.error(typeof d === 'string' ? d : String(d || 'Failed to add vendor product'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModernFormModal
      open={open}
      onClose={onClose}
      title="Add New Vendor Product"
      subtitle="Fill in the details below to add a new vendor product."
      headerVariant="light"
      headerSummary={
        vendorName ? (
          <>
            <div>
              <span className="mf-summary-label">Vendor:</span>{' '}
              <span className="mf-summary-value">{vendorName}</span>
            </div>
          </>
        ) : undefined
      }
      width={Math.min(typeof window !== 'undefined' ? window.innerWidth - 48 : 720, 720)}
      getContainer={getContainer}
      wrapClassName={wrapClassName}
      cancelText="Cancel"
      okText="Save vendor product"
      onOk={() => form.submit()}
      isSubmitting={loading}
      okDisabled={!vendorId}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
    >
      <Form form={form} layout="vertical" requiredMark onFinish={handleFinish} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Vendor catalog"
          description="Saves to Vendor products for this supplier and appears on purchase orders. Link a company product if you need goods receipt (GRPO) to update inventory."
        />

        <Divider orientation="left">Product details</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="name"
              label="Vendor item name"
              rules={[{ required: true, message: 'Enter the name as the supplier lists it' }]}
            >
              <Input placeholder="e.g. Premium rice 25kg bag" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="vendor_product_code" label="Vendor SKU / code">
              <Input placeholder="Supplier's code" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="unit_of_measure" label="Unit">
              <Input placeholder="pcs" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Pricing & supply</Divider>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="vendor_price"
              label="Buy price (per unit)"
              rules={[{ required: true, message: 'Enter price' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="minimum_order_quantity" label="MOQ">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="lead_time_days" label="Lead time (days)">
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="is_preferred" label="Preferred supplier" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">Company product link (optional)</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="product_id"
              label="Link to company product"
              tooltip="Required for receiving goods into inventory on this SKU."
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="No link — vendor-only line"
                options={products.map((p) => ({
                  value: p.id,
                  label: `${p.name} (${p.code})`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="description" label="Notes">
          <TextArea rows={2} placeholder="Internal note" />
        </Form.Item>

        <Card size="small" style={{ marginTop: 8, backgroundColor: '#f0f2f5' }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            After saving, pick this line on a PO row or add another with &quot;Add new vendor product&quot;.
          </Text>
        </Card>
      </Form>
    </ModernFormModal>
  );
};

export default QuickAddVendorProductModal;
